<template>
  <v-row
    no-gutters
    class="align-center py-1 handleDrag element"
    :class="classes"
    :style="{ 'background-color': backgroundColor }"
    @click="level === 1 && !isRight ? $emit('toggleHideGroup') : null"
  >
    <v-col v-if="level === 1 && !isRight" cols="auto">
      <v-icon>
        {{ isHidden ? 'mdi-menu-right' : 'mdi-menu-down' }}
      </v-icon>
    </v-col>
    <v-col cols="auto" v-if="level > 1 || isRight">
      <layer-style-preview-icon is-icon :layer-type="getLayerType(layer)" :layer-style="layerStyle" />
    </v-col>
    <v-col cols="auto" class="text-left" :class="{ 'pl-2': level > 1 || isRight, 'text--disabled': $attrs.disabled }">
      <v-row no-gutters>
        <v-col class="text-body-2 text-truncate">{{ name }}</v-col>
      </v-row>
      <v-row v-if="(level > 1 || isRight) && dataSourceVerboseName" no-gutters>
        <v-col class="font-weight-light font-italic" style="font-size: 12px; color: rgba(0, 0, 0, 0.59)">
          {{ $i18n.t('admin.datasource') }}: {{ dataSourceVerboseName }}
        </v-col>
      </v-row>
    </v-col>
    <slot name="appendName" v-bind="{ ...$props, ...$attrs }" />
    <v-col cols="auto" v-if="cacheSwitcher && hasCache && level > 1">
      <v-tooltip bottom>
        <template #activator="{ on }">
          <span v-on="on">
            <data-input
              :disabled="cacheDisabled"
              :data-type="{ name: 'switch' }"
              dense
              hide-details
              :value="cache"
              @change="changeCache"
            />
          </span>
        </template>
        {{
          cacheDisabled
            ? $i18n.t('admin.cacheDisabled')
            : cache
            ? $i18n.t('admin.switchOffCacheLayer')
            : $i18n.t('admin.switchOnCacheLayer')
        }}
      </v-tooltip>
    </v-col>
    <v-spacer />
    <v-col cols="auto" v-if="(!isRight && level === 1) || (hasLayerDotsItems && !isRight && level === 2)">
      <dots-menu :disabled="$attrs.disabled" font-size="19px" :items="dotsItems" v-on="listeners" />
    </v-col>
  </v-row>
</template>

<script>
import LayerStylePreviewIcon from '@/components/LayerStylePreviewIcon';
import DotsMenu from '@/components/DotsMenu';
import DataInput from '@/components/DataInput';
import { call, get } from 'vuex-pathify';

import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';

export default {
  name: 'DraggableListsMobileLayersItem',
  mixins: [stylePreviewUtilities],
  components: {
    LayerStylePreviewIcon,
    DotsMenu,
    DataInput,
  },
  props: {
    type: {
      type: String,
      default: '',
    },
    id: {
      type: Number,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    isRight: {
      type: Boolean,
      default: false,
    },
    level: {
      type: Number,
      required: true,
    },
    dotsItems: {
      type: Array,
      default: () => [
        {
          name: 'changeGroupName',
        },
        {
          name: 'delete',
        },
      ],
    },
    hasLayerDotsItems: {
      type: Boolean,
      default: false,
    },
    cache: {
      type: Boolean,
      default: false,
    },
    cacheSwitcher: {
      type: Boolean,
      default: false,
    },
    group_id: {
      type: [Number, String],
    },
    order: {
      type: Number,
    },
    scope: {
      type: String,
      default: 'core',
    },
    schema_scope: {
      type: String,
    },
    hasCache: {
      type: Boolean,
      default: false,
    },
    cacheDisabled: {
      type: Boolean,
      default: false,
    },
    isHidden: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    dataSources: get('layers/metadata'),
    dataSourceVerboseName() {
      return this.dataSources[this.layer.data_source_name]?.verbose_name || '';
    },
    layer: get('layers/layers@:id'),
    layerStyle() {
      if (this.layer) {
        return this.layer.style_web;
      }
      return undefined;
    },
    backgroundColor() {
      return this.level > 1 ? 'rgb(231, 231, 231, 0.5)' : 'white';
    },
    classes() {
      if (this.isRight) {
        return ['border', 'px-4'];
      }
      return this.level > 1 ? [`px-${this.level * 2}`, 'border'] : [];
    },
    listeners() {
      const listeners = {};
      for (const dotsItem of this.dotsItems) {
        listeners[dotsItem.name] = () => {
          this.$emit(dotsItem.name, this.$props);
        };
      }
      return listeners;
    },
  },
  methods: {
    attachLayerGroup: call('layers/attachLayerGroup'),
    getLayerType(layer) {
      return layer.type === 'features_layer' || layer.type === 'dynamic_features_layer'
        ? layer.geometry_type
        : layer.service_type;
    },
    changeCache(value) {
      this.attachLayerGroup({
        layer_id: this.id,
        body: {
          group_id: this.group_id,
          cache: value,
          order_in_group: this.order,
        },
        scope: this.scope,
        schema_scope: this.schema_scope,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.element {
  min-height: 38px;
}
.border {
  margin-bottom: 4px;
  @include border();
}
</style>
